import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { AsyncDownloadsService } from '@app-core/services/async-downloads/async-downloads.service';
import { DateService } from '@app-core/services/date/date.service';
import { DataService } from '@app-core/services/data/data.service';
import { SnackBarService } from '@app-core/services/snackbar/snack-bar.service';
import { TranslateService } from '@ngx-translate/core';
import { ReportsService } from '@app-reports/services/reports/reports.service';
import { AsyncReportType } from '@app-core/constants/constants';
import { RequestDownloadReportBody } from '@app-core/models/core.model';

interface SelfCoachingEffectivenessDownloadBody extends RequestDownloadReportBody {
  driverId: string;
  coachingSessionId: number;
  language?: string;
}

@Component({
  selector: 'app-self-coaching-effectiveness-report-dialog',
  templateUrl: './self-coaching-effectiveness-report-dialog.component.html',
  styleUrls: ['./self-coaching-effectiveness-report-dialog.component.scss'],
})
export class SelfCoachingEffectivenessReportDialogComponent implements OnInit, OnDestroy {
  public form!: FormGroup;
  public coachingSessionList: any[] = [];
  public sessionListLoader = false;
  public selectedSessionCreatedAt: any = null;
  public downloadReportLoader = false;
  public downloadSuccess: boolean | null = null;

  private readonly noOfDays = 180;
  private readonly ngUnsubscribe = new Subject<void>();

  constructor(
    private readonly fb: FormBuilder,
    private readonly reportsService: ReportsService,
    private readonly asyncDownloadsService: AsyncDownloadsService,
    private readonly dateService: DateService,
    public dataService: DataService,
    private readonly snackBarService: SnackBarService,
    private readonly translate: TranslateService,
    private readonly dialogRef: MatDialogRef<SelfCoachingEffectivenessReportDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: { reportType: string; title?: string; description?: string }
  ) {}

  public ngOnInit(): void {
    this.form = this.fb.group({
      driverId: ['', [Validators.required]],
      coachingSessionId: [null, [Validators.required]],
    });
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public close(): void {
    this.dialogRef.close(false);
  }

  public driverChanged(driverId: string = ''): void {
    this.form.patchValue({ driverId, coachingSessionId: null });
    this.downloadSuccess = null;
    this.coachingSessionList = [];
    this.selectedSessionCreatedAt = null;

    const coachingSessionControl = this.form.get('coachingSessionId');
    if (driverId) {
      coachingSessionControl?.enable();
      this.loadCoachingSessions(driverId);
    } else {
      coachingSessionControl?.disable();
    }
  }

  private loadCoachingSessions(driverId: string): void {
    const { from, to } = this.dateService.getDateRangeInISO(this.noOfDays);
    this.sessionListLoader = true;

    const params: any = {
      skip: 0,
      limit: 500,
      startDate: from,
      endDate: to,
      'driverIds[]': driverId,
      'coachingSessionTypes[]': 'SELF',
      coachingSessionStatus: 'COACHED',
    };

    this.reportsService
      .getCoachingSessionList(params)
      .pipe(
        finalize(() => {
          this.sessionListLoader = false;
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe({
        next: (res: any = {}) => {
          this.coachingSessionList = res?.rows || [];
        },
        error: () => {
          this.coachingSessionList = [];
        },
      });
  }

  public coachingSessionChange(event: MatSelectChange): void {
    const coachingSessionId = event?.value ?? null;
    this.form.patchValue({ coachingSessionId });
    this.selectedSessionCreatedAt = this.coachingSessionList.find((s) => s?.coachingSessionId === coachingSessionId)?.endTime;
    this.downloadSuccess = null;
  }

  public downloadReport(): void {
    this.downloadSuccess = null;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.downloadReportLoader = true;
    const { driverId, coachingSessionId } = this.form.value;
    const language = this.dataService._currentLanguage.getValue();

    const body: SelfCoachingEffectivenessDownloadBody = {
      reportType: AsyncReportType.selfCoachingEffectiveness.key,
      reportFormat: 'PDF',
      driverId,
      coachingSessionId,
      language,
    };

    this.asyncDownloadsService
      .requestDownloadReport(body)
      .pipe(
        finalize(() => {
          this.downloadReportLoader = false;
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe({
        next: () => {
          this.downloadSuccess = true;
        },
        error: (err: HttpErrorResponse) => {
          this.downloadSuccess = false;
          if (err?.status === 409) {
            this.snackBarService.failure(this.translate.instant('tripListComponentTripDuplicate'));
          } else {
            this.snackBarService.failure(this.translate.instant('tripListComponentTripsDownloadFailure'));
          }
        },
      });
  }

  public navigateToDownloads(): void {
    this.asyncDownloadsService.navigateToExportHistory(AsyncReportType.selfCoachingEffectiveness.key);
    this.close();
  }
}
